import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CompanySubscription } from '../../../shared/models';
import { ApiRootUrl } from '../../../shared/utils/api';
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { PlatformRootUrl } from '../../../shared/utils/platform';

interface CompanySubscriptionActionButtonProps {
	companyGuid: string | undefined;
	subscription: CompanySubscription;
	onEdit: (subscription: CompanySubscription) => void;
	onEditSuccessTiers: (subscription: CompanySubscription) => void;
	onUploadAgreement: (subscription: CompanySubscription) => void;
	onEditPaymentTerms: (subscription: CompanySubscription) => void;
	onDelete: (subscription: CompanySubscription) => void;
}

type PageActionItem = keyof typeof PageActionItemMap;
const PageActionItemMap = {
	Edit: 'Edit',
	Manage: 'Manage',
	SuccessTiers: 'Edit Tiers',
	ViewAgreement: 'View Agreement',
	PaymentTerms: 'Edit Payment Terms',
	UploadAgreement: 'Upload Agreement',
	Delete: 'Delete',
};

export const CompanySubscriptionActionButton: React.FC<CompanySubscriptionActionButtonProps> = ({
	companyGuid,
	subscription,
	onEdit,
	onEditSuccessTiers,
	onUploadAgreement,
	onEditPaymentTerms,
	onDelete,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const contractUploaded = subscription.agreementFileName && subscription.agreementFileName.length > 0;
	const paymentTermsAvailable = subscription.subscriptionFeePaymentSchedule == 'Custom';

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: PageActionItem) => {
		switch (action) {
			case 'Edit':
				onEdit(subscription);
				break;
			case 'Manage':
				window.open(`${PlatformRootUrl}/Emp/CompanySubscriptions/${companyGuid}`, '_blank');
				break;
			case 'SuccessTiers':
				onEditSuccessTiers(subscription);
				break;
			case 'ViewAgreement':
				window.open(
					`${ApiRootUrl}/Api/Companies/${companyGuid}/Subscriptions/${subscription?.guid}/Download`,
					'_blank'
				);
				break;
			case 'UploadAgreement':
				onUploadAgreement(subscription);
				break;
			case 'Delete':
				onDelete(subscription);
				break;
			case 'PaymentTerms':
				onEditPaymentTerms(subscription);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: PageActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{!contractUploaded && !subscription.isCancelled && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
											{PageActionItemMap['Edit']}
										</MenuItem>
									)}
									{contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Manage')}>
											{PageActionItemMap['Manage']}
										</MenuItem>
									)}
									{subscription.successFeeType == 'Tiered' && !contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'SuccessTiers')}>
											{PageActionItemMap['SuccessTiers']}
										</MenuItem>
									)}
									{paymentTermsAvailable && !contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'PaymentTerms')}>
											{PageActionItemMap['PaymentTerms']}
										</MenuItem>
									)}
									{contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ViewAgreement')}>
											{PageActionItemMap['ViewAgreement']}
										</MenuItem>
									)}
									{!contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'UploadAgreement')}>
											{PageActionItemMap['UploadAgreement']}
										</MenuItem>
									)}
									{!contractUploaded && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
											{PageActionItemMap['Delete']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
